/**
 * Maps a hotspot's authored `y` between "frame" space and the on-screen
 * `.imageWrapper` box (ED-345 QA: the component must fit within the
 * viewport).
 *
 * Authored `x/y` percentages are relative to the *uncapped* frame — the full
 * Figma aspect ratio at the current width (1440/810 desktop, 390/646
 * mobile). `.imageWrapper` caps its height at the viewport
 * (`ImageHotspot.module.scss`); the image stays `cover`ed onto that full
 * frame (`.imageFrame`), which the capped wrapper centers and crops top and
 * bottom equally — so `y%` of the capped box no longer lands on the same
 * point in the photo. `x` is unaffected (width is never cropped).
 *
 * The dots themselves are positioned by the CSS equivalent of
 * `frameToContainerY` (`HotspotDot.module.scss`, via container query units —
 * no JS on the render path). These JS versions exist for the two places that
 * have to agree with that render: `overlap.ts` (forward) and
 * `HotspotDebugOverlay` (inverse, so copied coordinates stay in frame space
 * regardless of the editor's monitor size). Keep all three in sync.
 */

/** Frame height / width per device — mirrors `.imageWrapper`'s
    `aspect-ratio` / `--frame-ratio` in `ImageHotspot.module.scss`. */
export const FRAME_RATIO = {
  desktop: 810 / 1440,
  mobile: 646 / 390,
} as const;

interface Box {
  width: number;
  height: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Frame-space `y` (0-100) → px from the top of the box. With no crop
 * (`height === width * frameRatio`) this is exactly `y%` of the height.
 * `inset` mirrors the CSS `clamp()` that keeps a dot near a cropped edge
 * fully on screen rather than clipped out of view.
 */
export const frameToContainerY = (
  y: number,
  { width, height }: Box,
  frameRatio: number,
  inset = 0
): number =>
  clamp(
    height / 2 + (y / 100 - 0.5) * width * frameRatio,
    inset,
    height - inset
  );

/** Inverse of `frameToContainerY` (without the inset clamp): px from the
    top of the box → frame-space `y`, clamped to 0-100. */
export const containerToFrameY = (
  top: number,
  { width, height }: Box,
  frameRatio: number
): number =>
  clamp(50 + ((top - height / 2) / (width * frameRatio)) * 100, 0, 100);
